var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import styled from 'styled-components';
import React, { useContext, useEffect, useState } from 'react';
import { cssColorValue, spacer16, spacer20, spacer40, spacer48, } from '@spotify-internal/encore-web';
import { CarouselNav } from '../CarouselNav';
import { CompactLayout, FullWidthLayout } from './Layout';
import { VideoCarouselContext } from './VideoCarouselContext';
import { VideoPlayer } from '../VideoPlayer';
import { MD_GRID_MAX_WIDTH, TWO_COLUMN_MAX_WIDTH, ONE_COLUMN_MAX_WIDTH, } from '../Grid';
import { getPoster } from '../VideoPlayer/utils';
const Wrapper = styled.div `
  display: flex;
  flex-direction: column;
  background-color: ${cssColorValue('backgroundBase')};
  color: ${cssColorValue('textBase')};
  height: 100%;
  width: 100%;
  gap: ${spacer20};
`;
const Carousel = styled.div `
  overflow: hidden;
  margin-inline: ${({ compact }) => (compact ? undefined : `-${spacer48}`)};

  @media (max-width: ${MD_GRID_MAX_WIDTH}) {
    margin-inline: ${({ compact }) => (compact ? undefined : `-${spacer40}`)};
  }
  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    margin-inline: -${spacer40};
  }
  @media (max-width: ${ONE_COLUMN_MAX_WIDTH}) {
    margin-inline: -${spacer16};
  }
`;
const Slide = styled.div `
  width: 100%;
  display: block;
  width: 100%;
  height: 100%;
  margin-top: 4px;
  margin-bottom: 4px;
  .placeholder {
    display: ${({ hide }) => (hide ? 'block' : `none`)};
  }
  .video {
    display: ${({ hide }) => (hide ? 'none' : `block`)};
  }
`;
const Video = styled.div `
  width: 100%;
  height: 100%;
`;
const FourVideoPlaceholder = styled.div `
  bottom: 0px;
  display: flex;
  margin-top: 4px;
  margin-bottom: 4px;
  width: 100%;
  position: relative;
  background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
  background-size: cover;
  cursor: pointer;
  :after {
    content: ' ';
    position: absolute;
    background: black;
    opacity: 60%;
    height: 100%;
    width: 100%;
  }
`;
export const VideoCarousel = ({ videos, useControlNumbers = true, numberOfVideosToShow = 5, compact = false, }) => {
    var _a;
    const { getVideoProps, carouselStartTimes } = useContext(VideoCarouselContext);
    const deviceHasHover = typeof window !== 'undefined'
        ? window.matchMedia('(hover: hover').matches
        : false;
    const canUseFullWidth = !compact && deviceHasHover;
    const isSmallScreen = typeof window !== 'undefined' ? (window === null || window === void 0 ? void 0 : window.innerWidth) <= 991 : false;
    const [isCompactCarousel, setIsCompactCarousel] = useState(!canUseFullWidth || isSmallScreen);
    const onResize = () => setIsCompactCarousel(isSmallScreen);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (canUseFullWidth) {
                window === null || window === void 0 ? void 0 : window.addEventListener('resize', onResize);
            }
            return () => {
                window === null || window === void 0 ? void 0 : window.removeEventListener('resize', onResize);
            };
        }
        return () => { };
    });
    const [hasInteracted, setHasInteracted] = useState(false);
    const videosInOrder = videos.map((video, videoIndex) => {
        return Object.assign(Object.assign({}, video), { videoIndex });
    });
    videosInOrder.unshift(videosInOrder.pop());
    videosInOrder.unshift(videosInOrder.pop());
    const [currentVideoIdx, setCurrentVideoIdx] = useState(2);
    const [isMoving, setIsMoving] = useState(false);
    const [scrollDistance, setScrollDistance] = useState(0);
    const getNewIndex = (pos) => pos > 0
        ? (currentVideoIdx + pos) % videos.length
        : (currentVideoIdx + pos + videos.length) % videos.length;
    const traverse = (pos) => __awaiter(void 0, void 0, void 0, function* () {
        if (!isMoving) {
            setIsMoving(true);
            const slidesWrapper = document.querySelector('.carousel-slides');
            const slides = document.querySelectorAll('.slide');
            const numSlides = slides.length;
            setScrollDistance(pos);
            slidesWrapper.onanimationend = () => {
                setCurrentVideoIdx(getNewIndex(pos));
                setScrollDistance(0);
                if (pos > 0) {
                    slidesWrapper.appendChild(slides[0]);
                    if (videos.length === 4) {
                        slidesWrapper.appendChild(slides[4]);
                    }
                }
                else {
                    if (videos.length === 4) {
                        slidesWrapper.prepend(slides[3]);
                    }
                    else
                        slidesWrapper.prepend(slides[numSlides - 1]);
                }
                setIsMoving(false);
            };
        }
    });
    let touchstartX = 0;
    let touchendX = 0;
    function checkForSwipe() {
        if (touchendX - touchstartX < -100) {
            traverse(1);
        }
        if (touchendX - touchstartX > 100) {
            traverse(-1);
        }
    }
    const getScrollAnimation = () => {
        return scrollDistance > 0 ? 'right' : 'left';
    };
    const nextAffordanceVideoIdx = (currentVideoIdx + 1) % videos.length;
    const nextNextAffordanceVideoIdx = (currentVideoIdx + 2) % videos.length;
    const prevAffordanceVideoIdx = (currentVideoIdx - 1 + videos.length) % videos.length;
    const prevPrevAffordanceVideoIdx = (currentVideoIdx - 2 + videos.length) % videos.length;
    const placeholderVideoIndex = videosInOrder[prevPrevAffordanceVideoIdx].videoIndex;
    const placeholderPosterPlaybackId = (_a = videos[placeholderVideoIndex].video.muxData) === null || _a === void 0 ? void 0 : _a.playbackId;
    const placeholderPosterStartTime = carouselStartTimes === null || carouselStartTimes === void 0 ? void 0 : carouselStartTimes[placeholderVideoIndex];
    const placeholderPoster = getPoster({
        useDefaultThumbnail: false,
        startTime: placeholderPosterStartTime,
        playbackId: placeholderPosterPlaybackId || '',
    });
    return (React.createElement(Wrapper, null,
        React.createElement(Carousel, { compact: isCompactCarousel, className: "carousel" }, isCompactCarousel ? (React.createElement(CompactLayout, { numVideos: videos.length > 4 ? videos.length : 5, className: "carousel-slides", scrollAnimation: scrollDistance === 0 ? undefined : getScrollAnimation() },
            videosInOrder.map((vid, idx) => {
                var _a;
                const isAffordanceVideo = idx === prevAffordanceVideoIdx ||
                    idx === nextAffordanceVideoIdx;
                const shouldPreload = isAffordanceVideo ||
                    idx === currentVideoIdx ||
                    idx === prevPrevAffordanceVideoIdx ||
                    idx === nextNextAffordanceVideoIdx;
                return (React.createElement(Slide, { className: "slide", hide: !isMoving && !isAffordanceVideo && currentVideoIdx !== idx },
                    React.createElement("div", { className: "placeholder", tabIndex: -1 }),
                    React.createElement(Video, { className: "video", key: `carousel-vid-${(_a = vid.video.muxData) === null || _a === void 0 ? void 0 : _a.playbackId}`, onTouchStart: e => {
                            touchstartX = e.changedTouches[0].screenX;
                        }, onTouchEnd: e => {
                            touchendX = e.changedTouches[0].screenX;
                            checkForSwipe();
                        }, onClick: () => {
                            if (isAffordanceVideo) {
                                if (idx === prevAffordanceVideoIdx) {
                                    traverse(-1);
                                }
                                else
                                    traverse(1);
                            }
                        }, onKeyDown: e => {
                            if (e.key === 'Enter' && isAffordanceVideo) {
                                if (idx === prevAffordanceVideoIdx) {
                                    traverse(-1);
                                }
                                else
                                    traverse(1);
                            }
                        } },
                        React.createElement(VideoPlayer, Object.assign({}, getVideoProps === null || getVideoProps === void 0 ? void 0 : getVideoProps(Object.assign(Object.assign({}, vid), {
                            autoplay: !isMoving && idx === currentVideoIdx,
                            disabled: isMoving && scrollDistance
                                ? idx !== getNewIndex(scrollDistance)
                                : idx !== currentVideoIdx,
                            playOnHover: false,
                            toggleVolumeOnClick: !isAffordanceVideo,
                            preload: shouldPreload ? 'auto' : 'none',
                        })))))));
            }),
            videos.length === 4 && (React.createElement(FourVideoPlaceholder, { className: "slide", backgroundImage: placeholderPoster, tabIndex: -1 })))) : (React.createElement(FullWidthLayout, { numberOfVideosToShow: numberOfVideosToShow, className: "carousel-slides" }, videosInOrder.map((vid, idx) => {
            var _a;
            if (idx > numberOfVideosToShow + 1) {
                return null;
            }
            const isAffordanceVideo = idx === currentVideoIdx - 1 ||
                idx === numberOfVideosToShow + currentVideoIdx;
            const autoplay = idx === currentVideoIdx && !hasInteracted;
            return (React.createElement(Video, { className: "video", key: `carousel-vid-${(_a = vid.video.muxData) === null || _a === void 0 ? void 0 : _a.playbackId}`, onFocus: () => {
                    if (!hasInteracted)
                        setHasInteracted(true);
                }, onMouseEnter: () => {
                    if (idx !== 1 && !hasInteracted)
                        setHasInteracted(true);
                }, onMouseLeave: () => {
                    if (!hasInteracted)
                        setHasInteracted(true);
                }, onClick: () => {
                    if (isAffordanceVideo) {
                        if (idx === 1) {
                            traverse(-2);
                        }
                        else
                            traverse(2);
                    }
                }, onKeyDown: e => {
                    if (e.key === 'Enter' && isAffordanceVideo) {
                        if (idx === 1) {
                            traverse(-2);
                        }
                        else
                            traverse(2);
                    }
                } },
                React.createElement(VideoPlayer, Object.assign({}, getVideoProps === null || getVideoProps === void 0 ? void 0 : getVideoProps(Object.assign({ autoplay: autoplay, videoIndex: idx < 2 ? videos.length + idx - 2 : idx - 2 }, {
                    disabled: isAffordanceVideo,
                    disabledUntilHover: autoplay ? hasInteracted : true,
                    toggleVolumeOnClick: !isAffordanceVideo,
                    playOnHover: !autoplay,
                }))))));
        })))),
        React.createElement(CarouselNav, { allowLooping: true, index: (currentVideoIdx || videos.length) - 1 || videos.length, total: videos.length, handlePrev: () => traverse(-1), handleNext: () => traverse(1), useControlNumbers: useControlNumbers })));
};
//# sourceMappingURL=index.js.map